import React, { useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { LineItem, Order, DraftOrder } from '../../types/orders';
import { colors, distances, border } from '../../styles/constants';
import Card from '../../components/Card';
import { H3, Muted } from '../../components/Typography';
import { Table, Tr, Th, Thead, Tbody, Td } from '../../components/Table';
import { Money } from '../../components/Formatters';
import { groupItems } from '../helpers';
import Icon from '../../components/Icons';
import { Dl, Dt, Dd } from '../../components/DescriptionList/Vertical';
import ExpandCollapseJson from './ExpandCollapseJson';
import Hr from '../../components/Hr';
const getVatPercentages = (item: LineItem) => item.tax_lines.map((taxItem) => `${taxItem.percentage}%`).join(', ');

const getVatSum = (item: LineItem) =>
    item.tax_lines.map((taxItem) => taxItem.amount).reduce((acc, elem) => acc + elem) || 0;

export interface ItemsProps extends WithTranslation {
    draftOrOrder: Order | DraftOrder;
}

export interface DraftOrderItemProps {
    item: LineItem;
    currency: string;
    t: (key: string) => string;
}

const Item = (
    {
        t,
        item,
        currency,
    }: DraftOrderItemProps
) => {
    const [isExpanded, toggle] = useState(false);
    const expandable = item.store || item.salesperson_name || item.salesperson_id || item.status || item.external_id;
    return (
        <>
            <Tr onClick={() => toggle(!isExpanded)}>
                <Td>{item.line_id}</Td>
                <Td>{item.id}</Td>
                <Td>{item.description}</Td>
                <Td>{item.description_alias}</Td>
                {item.quantity !== undefined ? (
                    <Td>{item.quantity}</Td>
                ) : (
                    <Td className="align-center">
                        <Muted>–</Muted>
                    </Td>
                )}

                <Td className="vat collapse-650">{getVatPercentages(item)}</Td>
                <Td className="vat collapse-650">
                    <Money amount={getVatSum(item)} currency={currency} />
                </Td>
                <Td className="align-right amount">
                    {item.net_amount && <Money amount={item.net_amount} currency={currency} />}
                </Td>
                <Td>
                    <Icon
                        icon="caret_down"
                        fill={colors.text}
                        className={['clickable', isExpanded && 'flip'].filter((x) => x).join(' ')}
                    ></Icon>
                </Td>
            </Tr>
            {isExpanded && (
                <Tr className="no-highlight alt">
                    <Td colSpan={9}>
                        <Pad>
                            {expandable && (
                                <ItemDetailColumn>
                                    {item.store && item.store.name && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.store.name')}</Dt>
                                            <Dd>{item.store.name}</Dd>
                                        </Dl>
                                    )}
                                    {item.store && item.store.business_name && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.store.business_name')}</Dt>
                                            <Dd>{item.store.business_name}</Dd>
                                        </Dl>
                                    )}
                                    {item.store && item.store.id && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.store.id')}</Dt>
                                            <Dd>
                                                <code>{item.store.id}</code>
                                            </Dd>
                                        </Dl>
                                    )}
                                    {item.store && item.store.organization_number && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.store.organization_number')}</Dt>
                                            <Dd>{item.store.organization_number}</Dd>
                                        </Dl>
                                    )}
                                    {item.store && item.store.comment && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.store.comment')}</Dt>
                                            <Dd>{item.store.comment}</Dd>
                                        </Dl>
                                    )}
                                    {item.salesperson_name && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.salesperson.name')}</Dt>
                                            <Dd>{item.salesperson_name}</Dd>
                                        </Dl>
                                    )}
                                    {item.salesperson_id && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.salesperson.id')}</Dt>
                                            <Dd>
                                                <code>{item.salesperson_id}</code>
                                            </Dd>
                                        </Dl>
                                    )}
                                    {item.status && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.status')}</Dt>
                                            <Dd>
                                                <code>{item.status}</code>
                                            </Dd>
                                        </Dl>
                                    )}
                                    {item.external_id && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.external_id')}</Dt>
                                            <Dd>
                                                <code>{item.external_id}</code>
                                            </Dd>
                                        </Dl>
                                    )}
                                </ItemDetailColumn>
                            )}
                            {item.dimension && (
                                <ItemDetailColumn>
                                    {item.dimension.color && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.dimension.color')}</Dt>
                                            <Dd>{item.dimension.color}</Dd>
                                        </Dl>
                                    )}
                                    {item.dimension.size && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.dimension.size')}</Dt>
                                            <Dd>{item.dimension.size}</Dd>
                                        </Dl>
                                    )}
                                    {item.dimension.style && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.dimension.style')}</Dt>
                                            <Dd>{item.dimension.style}</Dd>
                                        </Dl>
                                    )}
                                    {item.dimension.config && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.dimension.config')}</Dt>
                                            <Dd>{item.dimension.config}</Dd>
                                        </Dl>
                                    )}
                                    {item.dimension.variant && (
                                        <Dl>
                                            <Dt>{t('orders.fields.order_items.dimension.variant')}</Dt>
                                            <Dd>{item.dimension.variant}</Dd>
                                        </Dl>
                                    )}
                                </ItemDetailColumn>
                            )}
                            {item.groups && (
                                <ItemDetailColumn>
                                    <Dl>
                                        <Dt>{t('orders.fields.order_items.groups')}</Dt>
                                        {item.groups.map((g) => (
                                            <DetailBox key={g.id || g.name}>
                                                {g.id && (
                                                    <Dl>
                                                        <Dt>{t('orders.fields.order_items.group.id')}</Dt>
                                                        <Dd>{g.id}</Dd>
                                                    </Dl>
                                                )}
                                                {g.name && (
                                                    <Dl>
                                                        <Dt>{t('orders.fields.order_items.group.name')}</Dt>
                                                        <Dd>{g.name}</Dd>
                                                    </Dl>
                                                )}
                                            </DetailBox>
                                        ))}
                                    </Dl>
                                </ItemDetailColumn>
                            )}

                            {(item.related_item || item.reversed_reason) && (
                                <ItemDetailColumn>
                                    {item.related_item && (
                                        <Dl>
                                            <Dt>
                                                {t(`orders.fields.order_items.related_item.${item.related_item.type}`)}
                                            </Dt>
                                            <Dd>{item.related_item.line_id}</Dd>
                                        </Dl>
                                    )}
                                    {item.reversed_reason && (
                                        <Dl>
                                            <Dt>{t(`orders.fields.order_items.reversed_reason`)}</Dt>
                                            <Dd>{item.reversed_reason}</Dd>
                                        </Dl>
                                    )}
                                </ItemDetailColumn>
                            )}
                            {item.discount_lines && item.discount_lines.length && (
                                <ItemDetailColumn>
                                    <Dt>{t('orders.fields.order_items.discount.discounts')}</Dt>
                                    {item.discount_lines.map((discount, i) => (
                                        <DetailBox key={i}>
                                            {discount.discount_id && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.discount.discount_id')}</Dt>
                                                    <Dd>{discount.discount_id}</Dd>
                                                </Dl>
                                            )}
                                            {discount.description && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.discount.description')}</Dt>
                                                    <Dd>{discount.description}</Dd>
                                                </Dl>
                                            )}
                                            {discount.discount_type && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.discount.discount_type')}</Dt>
                                                    <Dd>{discount.discount_type}</Dd>
                                                </Dl>
                                            )}
                                            {discount.percentage && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.discount.percentage')}</Dt>
                                                    <Dd>{discount.percentage}%</Dd>
                                                </Dl>
                                            )}
                                            {discount.amount && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.discount.amount')}</Dt>
                                                    <Dd>
                                                        <Money amount={discount.amount} currency={currency} />
                                                    </Dd>
                                                </Dl>
                                            )}
                                            {discount.line_id && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.discount.line_id')}</Dt>
                                                    <Dd>{discount.line_id}</Dd>
                                                </Dl>
                                            )}
                                        </DetailBox>
                                    ))}
                                </ItemDetailColumn>
                            )}
                            {item.additions && item.additions.length && (
                                <ItemDetailColumn>
                                    <Dt>{t('orders.fields.order_items.addition.additions')}</Dt>
                                    {item.additions.map((addition, i) => (
                                        <DetailBox key={i}>
                                            {addition.id && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.addition.id')}</Dt>
                                                    <Dd>{addition.id}</Dd>
                                                </Dl>
                                            )}
                                            {addition.description && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.addition.description')}</Dt>
                                                    <Dd>{addition.description}</Dd>
                                                </Dl>
                                            )}
                                            {addition.quantity && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.addition.quantity')}</Dt>
                                                    <Dd>{addition.quantity}</Dd>
                                                </Dl>
                                            )}
                                            {addition.gross_amount && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.addition.amount')}</Dt>
                                                    <Dd>
                                                        <Money amount={addition.gross_amount} currency={currency} />
                                                    </Dd>
                                                </Dl>
                                            )}
                                            {addition.groups && addition.groups.length && (
                                                <Dl>
                                                    <Dt>{t('orders.fields.order_items.addition.groups')}</Dt>
                                                    {addition.groups.map((group, j) => (
                                                        <DetailBox key={j}>
                                                            {group.id && (
                                                                <Dl>
                                                                    <Dt>
                                                                        {t(
                                                                            'orders.fields.order_items.addition.group.id'
                                                                        )}
                                                                    </Dt>
                                                                    <Dd>
                                                                        <code>{group.id}</code>
                                                                    </Dd>
                                                                </Dl>
                                                            )}
                                                            {group.name && (
                                                                <Dl>
                                                                    <Dt>
                                                                        {t(
                                                                            'orders.fields.order_items.addition.group.name'
                                                                        )}
                                                                    </Dt>
                                                                    <Dd>
                                                                        <code>{group.name}</code>
                                                                    </Dd>
                                                                </Dl>
                                                            )}
                                                        </DetailBox>
                                                    ))}
                                                </Dl>
                                            )}
                                        </DetailBox>
                                    ))}
                                </ItemDetailColumn>
                            )}
                        </Pad>
                        {item.comment && (
                            <Pad>
                                <ItemDetailColumn>
                                    <Dl>
                                        <Dt>{t('orders.fields.order_items.comment')}</Dt>
                                        <Dd>{item.comment}</Dd>
                                    </Dl>
                                </ItemDetailColumn>
                            </Pad>
                        )}
                        {item.metadata && (
                            <Pad>
                                <WholeWidthColumn>
                                    <ExpandCollapseJson
                                        label={<strong>{t('orders.fields.click_to_expand_metadata')}</strong>}
                                        json={item.metadata}
                                        background={colors.background}
                                    />
                                </WholeWidthColumn>
                            </Pad>
                        )}
                    </Td>
                </Tr>
            )}
        </>
    );
};

const getItems = (draftOrOrder: DraftOrder | Order) => {
    if ((draftOrOrder as DraftOrder).order) {
        return (draftOrOrder as DraftOrder).order.items || [];
    }
    if ((draftOrOrder as Order).items) {
        return (draftOrOrder as Order).items || [];
    }
    return [];
};
const getCurrency = (draftOrOrder: DraftOrder | Order) => {
    if ((draftOrOrder as DraftOrder).order) {
        return (draftOrOrder as DraftOrder).order.currency;
    }
    if ((draftOrOrder as Order).items) {
        return (draftOrOrder as Order).currency;
    }
    return '';
};
const getNetAmount = (draftOrOrder: DraftOrder | Order) => {
    if ((draftOrOrder as DraftOrder).order) {
        return (draftOrOrder as DraftOrder).order.net_amount || 0;
    }
    if ((draftOrOrder as Order).items) {
        return (draftOrOrder as Order).net_amount || 0;
    }
    return 0;
};

const Items = (
    {
        t,
        draftOrOrder,
    }: ItemsProps
) => {
    const currency = getCurrency(draftOrOrder);
    const items = groupItems(getItems(draftOrOrder));
    return (
        <Card marginBottom={distances.normal}>
            <H3>{t('orders.fields.items')}</H3>
            <Hr />
            <DraftOrderItemsTable className="no-wrap highlight inline">
                <Thead>
                    <Tr>
                        <Th>{t('orders.fields.line_item_id')}</Th>
                        <Th>{t('orders.fields.item_id')}</Th>
                        <Th>{t('orders.fields.description')}</Th>
                        <Th>{t('orders.fields.details')}</Th>
                        <Th>{t('orders.fields.quantity')}</Th>
                        <Th className="vat collapse-650">{t('orders.fields.vat_percentage')}</Th>
                        <Th className="vat collapse-650">{t('orders.fields.vat')}</Th>
                        <Th className="align-right amount">{t('orders.fields.amount')}</Th>
                        <Th></Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {items.map((item, i) => (
                        <Item t={t} item={item} currency={currency} key={`${item.line_id}-----${i}`} />
                    ))}
                    <SummingRow>
                        <Td>{t('orders.fields.total')}</Td>
                        <Td />
                        <Td />
                        <Td />
                        <Td />
                        <Td className="collapse-650" />
                        <Td className="vat collapse-650" />
                        <Td className="align-right amount">
                            <Money amount={getNetAmount(draftOrOrder)} currency={currency} />
                        </Td>
                        <Td />
                    </SummingRow>
                </Tbody>
            </DraftOrderItemsTable>
        </Card>
    );
};



const DraftOrderItemsTable = styled(Table)`
    .amount,
    .vat {
        white-space: nowrap;
    }
`;

const DetailBox = styled.div`
    padding: ${distances.small};
    margin-bottom: ${distances.small};
    display: flex;
    flex-direction: column;
    border: ${border.normal} solid ${colors.background};
    font-size: 11px;
`;

const Pad = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;
const ItemDetailColumn = styled.div`
    display: flex;
    flex-direction: column;
    margin: ${distances.tiny};
`;
const WholeWidthColumn = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin: ${distances.tiny};
`;

const SummingRow = styled.tr`
    border-top: ${border.normal} solid ${colors.borderLight};
    border-bottom: ${border.normal} solid ${colors.borderLight};
`;

export default withTranslation()(Items);
